import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { AppRouter } from '@/app/router';
import { initAdapters, configureAuth } from '@/lib/platform';
import { isBenignAbortError } from '@/lib/observability/ignore-errors';
import { useAuthStore } from '@/stores/use-auth-store';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN_LUMEN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    // Drop deliberate fetch-cancel AbortErrors — they surface as unhandled
    // rejections during React unmount but are never actionable (LUMEN-44).
    beforeSend: (event, hint) => (isBenignAbortError(hint?.originalException) ? null : event),
  });
}


// Build-time guard: Auth0 must be wired up before the app boots, otherwise
// every sign-in attempt throws at the first /authorize redirect. Fail loudly.
if (!import.meta.env.VITE_AUTH0_DOMAIN) {
  throw new Error('VITE_AUTH0_DOMAIN must be set at build time');
}
if (!import.meta.env.VITE_AUTH0_CLIENT_ID) {
  throw new Error('VITE_AUTH0_CLIENT_ID must be set at build time');
}

// Suppress noisy warn-only Yjs premature-access warning from the Lexical
// collaboration plugin. It fires during paste/insert when a new XmlText node
// is read before being inserted into the Yjs doc — harmless, upstream bug.
const _warn = console.warn.bind(console);
console.warn = (...args: unknown[]) => {
  if (typeof args[0] === 'string' && args[0].includes('Add Yjs type to a document before reading data')) return;
  _warn(...args);
};

if (import.meta.env.DEV) {
  Promise.all([
    import('@/stores/use-ui-store'),
    import('@/stores/use-auth-store'),
    import('@/stores/use-calendar-store'),
    import('@/stores/use-workspace-store'),
  ]).then(([ui, auth, calendar, workspaceStore]: Array<Record<string, unknown>>) => {
    const w = window as unknown as Record<string, unknown>;
    w.useUIStore = (ui as Record<string, unknown>).useUIStore;
    w.useAuthStore = (auth as Record<string, unknown>).useAuthStore;
    w.useCalendarStore = (calendar as Record<string, unknown>).useCalendarStore;
    w.useWorkspaceStore = (workspaceStore as Record<string, unknown>).useWorkspaceStore;
  });
}

// Wire apiFetch to the auth store so every authenticated API call from the
// React tree (listWorkspaces, etc.) automatically attaches Authorization:
// Bearer <accessToken>. Without this every request goes out unauthenticated,
// nexus returns 401, refreshFromServer marks bootstrapped with no workspaces,
// and the user lands in OnboardingFlow forever. Lost during the Clerk→Auth0
// migration when the original wire-up (in useClerkSync) was deleted.
configureAuth(() => useAuthStore.getState().accessToken);

async function bootstrap() {
  await initAdapters();

  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <AppRouter />,
  );
}

bootstrap().catch((err) => {
  Sentry.captureException(err);
  console.error('[main] Bootstrap failed:', err);
});
